import axios from 'axios';
import { apiClient } from 'services/api/apiClient';
import { withMediaToken } from 'utils/media';
import type { SpreadsheetDocument } from 'models/SpreadsheetDocument';

export interface InspectionReference {
  source?: 'network' | 'material';
  quantity_mode?: 'column' | 'rows' | 'ip_range';
  ip_column?: string;
  end_ip_column?: string;
  document_id: string;
  sha256: string;
  name_column: string;
  quantity_column: string;
  floor_column?: string;
  unit_column?: string;
  types: Record<string, string>;
}

export interface ReferenceOption {
  document: SpreadsheetDocument;
  sha256: string;
}

export interface QuantityCheck {
  status: 'match' | 'shortage' | 'excess' | 'unavailable' | 'not_applicable';
  actual?: number;
  difference?: number;
  reason?: string;
}

export interface ReferenceComparison {
  names: string[];
  rows: number[];
  kind: string;
  floor: string;
  unit: string;
  expected: number;
  map: QuantityCheck;
  nvr: QuantityCheck;
  materials: QuantityCheck;
}

/** 공통 설치 킷에 담을 것. 현장과 무관하게 서버 전체에서 한 벌이다. */
export interface CommonKitInput {
  include_images: boolean;
  include_os_usb: boolean;
  /** offline-manage 의 .deb 번들(docker·nginx·zerotier)을 킷에 담는다. */
  include_debs: boolean;
  /** 월패드 지도 이미지 생성 도구(node·크롬·생성기, bundle/wallpad-map)를 담는다. */
  include_wallpad: boolean;
}

export interface UsbKitEntry {
  path: string;
  note: string;
  bytes: number;
}

/** 현장 패키지(usb_kit) 또는 공통 킷(site_code 빈 값)의 결과. */
export interface UsbKit {
  built_at: string;
  site_code: string;
  filename: string;
  sha256: string;
  bytes: number;
  entries: UsbKitEntry[];
  warnings: string[];
  os_included: boolean;
}

/** 공통 킷의 최근 제작 시도와, 받을 수 있는 마지막 완성 킷. */
export interface CommonKitStatus {
  status: 'idle' | 'running' | 'completed' | 'failed';
  input?: CommonKitInput;
  started_at?: string;
  finished_at?: string;
  error?: string;
  kit?: UsbKit;
}

export interface AgentInput {
  reference?: InspectionReference;
  kind: 'inspection' | 'parking_recovery' | 'usb_kit' | 'site_health';
  filename?: string;
  layer?: string;
  block?: string;
}

export interface AgentRun<T = unknown> {
  id: string;
  site_id: number;
  kind: AgentInput['kind'];
  status: 'queued' | 'running' | 'completed' | 'failed';
  input: AgentInput;
  result?: T;
  error?: string;
  created_at: string;
  updated_at: string;
}

export interface InspectionFinding {
  code: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  source: string;
  document_id?: string;
  row?: number;
  map_id?: number;
  floor?: string;
  object_id?: string;
}

export interface SiteInspection {
  reference?: {
    source?: 'network' | 'material';
    quantity_mode?: 'column' | 'rows' | 'ip_range';
    document_id: string;
    name: string;
    sha256: string;
    excluded_rows: number;
    comparisons: ReferenceComparison[];
  };
  checked_at: string;
  coverage: 'complete' | 'partial';
  documents: number;
  channels: number;
  cameras: number;
  parkings: number;
  roi_files: number;
  findings: InspectionFinding[];
  sources: { name: string; sha256: string; updated_at: string }[];
}

export interface ParkingCandidate {
  id: string;
  vertices: [number, number][];
  method: 'closed_lines' | 'triangle_pair' | 'notched_lines';
  width: number;
  depth: number;
  area: number;
  /** 빗금 통로가 붙은 칸만 장애인, 선으로 그린 작은 사각형은 기둥 — 이 필드가 생기기 전 저장된 결과에는 없다(일반으로 본다). */
  parking_type?: 'p-parking-basic' | 'p-parking-disable' | 's-pillar';
}

export interface ParkingRecovery {
  filename: string;
  layer: string;
  block: string;
  sha256: string;
  units: number;
  line_count: number;
  candidates: ParkingCandidate[];
  background: [number, number][][];
  warnings: string[];
}

const base = (siteId: number) => `/v0.1/parking-agent/sites/${siteId}/runs`;
const COMMON_KIT = '/v0.1/parking-agent/common-kit';

export const ParkingAgentService = {
  async references(
    siteId: number,
    signal?: AbortSignal,
    source: 'network' | 'material' = 'material',
  ): Promise<ReferenceOption[]> {
    return (
      await apiClient.get<ReferenceOption[]>(
        `/v0.1/parking-agent/sites/${siteId}/inspection-references`,
        { signal, params: { source } },
      )
    ).data;
  },
  async start<T>(siteId: number, input: AgentInput): Promise<AgentRun<T>> {
    return (await apiClient.post<AgentRun<T>>(base(siteId), input)).data;
  },
  async list(siteId: number, signal?: AbortSignal): Promise<AgentRun[]> {
    return (await apiClient.get<AgentRun[]>(base(siteId), { signal })).data;
  },
  async get<T>(
    siteId: number,
    id: string,
    signal?: AbortSignal,
  ): Promise<AgentRun<T>> {
    return (
      await apiClient.get<AgentRun<T>>(
        `${base(siteId)}/${encodeURIComponent(id)}`,
        { signal },
      )
    ).data;
  },
  // 번들은 GB 단위라 blob 으로 받지 않고, 인증 토큰을 실은 URL 로 브라우저가 직접 받는다.
  usbKitDownloadUrl(siteId: number, runId: string): string {
    return withMediaToken(`${base(siteId)}/${encodeURIComponent(runId)}/download`);
  },
  async commonKit(signal?: AbortSignal): Promise<CommonKitStatus> {
    return (await apiClient.get<CommonKitStatus>(COMMON_KIT, { signal })).data;
  },
  async startCommonKit(input: CommonKitInput): Promise<CommonKitStatus> {
    return (await apiClient.post<CommonKitStatus>(COMMON_KIT, input)).data;
  },
  commonKitDownloadUrl(): string {
    return withMediaToken(`${COMMON_KIT}/download`);
  },
};

export function agentError(error: unknown): string {
  if (axios.isAxiosError(error))
    return error.response?.data?.message || error.message;
  return error instanceof Error ? error.message : '작업을 완료하지 못했습니다.';
}
